import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, ArrowUpRight, X } from "lucide-react";
import analytics from "@/utils/analytics";

// ─── shared taxonomy labels (kept in sync with ReviewsTaggerPage) ───────

const INDUSTRY_LABELS = {
  hospitality: "Hospitality",
  retail: "Retail",
  services: "Services",
  "e-commerce": "E-Commerce",
  fitness: "Fitness",
  "pet-services": "Pet Services",
  beauty: "Beauty",
  partnership: "Partnership",
};

const PRODUCT_LABELS = {
  "card-machines": "Card Machines",
  "payment-gateway": "Payment Gateway",
  "epos-systems": "EPOS Systems",
  "online-payments": "Online Payments",
  "online-ordering": "Online Ordering",
  "virtual-terminal": "Phone Payments",
  "merchant-cash-advance": "Merchant Cash Advance",
  partners: "Partners",
};

// ─── atoms ──────────────────────────────────────────────────────────────

export function Stars({ rating = 5, size = 14 }) {
  const n = Math.max(0, Math.min(5, Math.round(Number(rating) || 5)));
  return (
    <div className="flex items-center gap-0.5" aria-label={`${n} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((s) => (
        <Star
          key={s}
          size={size}
          className={s <= n ? "fill-amber-400 text-amber-400" : "text-brand-light-grey"}
        />
      ))}
    </div>
  );
}

export function GoogleG({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-label="Google" role="img" className="flex-shrink-0">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
    </svg>
  );
}

export function TrustpilotStar({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-label="Trustpilot" role="img" className="flex-shrink-0">
      <rect width="24" height="24" rx="3" fill="#00B67A" />
      <path d="M12 4l2.47 5 5.53.8-4 3.9.95 5.5L12 16.6 7.05 19.2 8 13.7l-4-3.9 5.53-.8z" fill="#fff" />
    </svg>
  );
}

function SourceBadge({ source, size = 12 }) {
  const Icon = source === "trustpilot" ? TrustpilotStar : GoogleG;
  const label = source === "trustpilot" ? "Trustpilot" : "Google";
  return (
    <div className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-brand-grey">
      <Icon size={size} />
      <span>{label}</span>
    </div>
  );
}

function Avatar({ name, size = "md" }) {
  const initial = (name || "G").trim().charAt(0).toUpperCase();
  const cls = size === "sm" ? "w-7 h-7 text-xs" : "w-9 h-9 text-sm";
  return (
    <div
      className={`${cls} rounded-full bg-brand-aqua-light flex items-center justify-center text-brand-aqua-dark font-heading font-bold flex-shrink-0`}
      aria-hidden="true"
    >
      {initial}
    </div>
  );
}

// ─── tag pills ──────────────────────────────────────────────────────────

// ─── tag pill routing ──────────────────────────────────────────────────
// Each taxonomy key maps to its best-fit landing page. The "services"
// industry tag routes to /industries/personal-care (the canonical slug).
// The "partnership" industry and "partners" product both point to the
// partner programme where the relevant RelatedReviews strip lives.

const INDUSTRY_ROUTES = {
  hospitality: "/industries/hospitality",
  retail: "/industries/retail",
  services: "/industries/personal-care",
  "e-commerce": "/industries/e-commerce",
  fitness: "/industries/fitness",
  "pet-services": "/industries/pet-services",
  beauty: "/industries/beauty",
  partnership: "/partner-programme",
};

const PRODUCT_ROUTES = {
  "card-machines": "/card-machines",
  "payment-gateway": "/payment-gateway",
  "epos-systems": "/epos-systems",
  "online-payments": "/online-payments",
  "online-ordering": "/online-ordering",
  "virtual-terminal": "/virtual-terminal",
  "merchant-cash-advance": "/merchant-cash-advance",
  partners: "/partner-programme",
};

function TagRow({ industries = [], products = [], compact = false, onNavigate }) {
  if (industries.length === 0 && products.length === 0) return null;
  const pad = compact ? "px-2 py-0.5 text-[10px]" : "px-2.5 py-1 text-[11px]";

  function clickHandler(type, key) {
    return (e) => {
      // Prevent modal close from the backdrop click handler when a tag
      // inside the modal is clicked.
      e.stopPropagation();
      analytics.reviewTagClicked(type, key);
      if (onNavigate) onNavigate();
    };
  }

  return (
    <div className="flex flex-wrap gap-1.5">
      {industries.map((k) => {
        const to = INDUSTRY_ROUTES[k];
        const label = INDUSTRY_LABELS[k] || k;
        const base = `${pad} font-semibold rounded-full bg-brand-aqua-light text-brand-aqua-dark`;
        if (!to) {
          return (
            <span key={`i-${k}`} className={base} data-testid={`tag-industry-${k}`}>
              {label}
            </span>
          );
        }
        return (
          <Link
            key={`i-${k}`}
            to={to}
            onClick={clickHandler("industry", k)}
            className={`${base} hover:bg-brand-aqua hover:text-white transition-colors`}
            data-testid={`tag-industry-${k}`}
            aria-label={`See reviews and details for ${label}`}
          >
            {label}
          </Link>
        );
      })}
      {products.map((k) => {
        const to = PRODUCT_ROUTES[k];
        const label = PRODUCT_LABELS[k] || k;
        const base = `${pad} font-semibold rounded-full bg-brand-pink-light text-brand-pink`;
        if (!to) {
          return (
            <span key={`p-${k}`} className={base} data-testid={`tag-product-${k}`}>
              {label}
            </span>
          );
        }
        return (
          <Link
            key={`p-${k}`}
            to={to}
            onClick={clickHandler("product", k)}
            className={`${base} hover:bg-brand-pink hover:text-white transition-colors`}
            data-testid={`tag-product-${k}`}
            aria-label={`See reviews and details for ${label}`}
          >
            {label}
          </Link>
        );
      })}
    </div>
  );
}

// ─── modal ──────────────────────────────────────────────────────────────

function ReviewModal({ review, onClose, onSourceClick }) {
  // Lock body scroll while open; restore on close.
  useEffect(() => {
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    function onKey(e) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = original;
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Customer review"
      data-testid="review-modal"
    >
      <div
        className="relative w-full max-w-xl max-h-[85vh] overflow-y-auto bg-white rounded-2xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 p-2 rounded-full text-brand-grey hover:bg-brand-light-grey/60 hover:text-brand-black transition-colors"
          aria-label="Close review"
          data-testid="review-modal-close"
        >
          <X size={18} />
        </button>

        <div className="p-6 sm:p-8">
          <div className="flex items-center gap-3 mb-4">
            <Stars rating={review.rating} size={16} />
            <SourceBadge source={review.source} size={14} />
          </div>

          {review.title && (
            <h3 className="font-heading text-xl sm:text-2xl font-bold text-brand-black leading-tight mb-4">
              {review.title}
            </h3>
          )}

          <p className="text-[15px] text-brand-black/90 leading-relaxed whitespace-pre-line mb-6">
            {review.text}
          </p>

          <TagRow
            industries={review.industries || []}
            products={review.products || []}
            onNavigate={onClose}
          />

          <div className="mt-6 pt-5 border-t border-brand-light-grey/60 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <Avatar name={review.author} />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-brand-black truncate">
                  {review.author}
                </p>
                {review.dateLabel && (
                  <p className="text-[11px] text-brand-grey">{review.dateLabel}</p>
                )}
              </div>
            </div>
            <a
              href={review.profileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs font-semibold text-brand-aqua-dark hover:text-brand-black underline underline-offset-4 whitespace-nowrap"
              data-testid="review-modal-source-link"
              onClick={() => {
                if (onSourceClick) onSourceClick(review);
              }}
            >
              {`View on ${review.source === "trustpilot" ? "Trustpilot" : "Google"}`}
              <ArrowUpRight size={12} />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── card ───────────────────────────────────────────────────────────────

// Paragraph-first preview: if the text has multiple paragraphs, show only
// the first one. If it's one long block, show it and rely on line-clamp.
function firstParagraph(text) {
  if (!text) return "";
  const parts = text.split(/\n{2,}/);
  return parts[0].trim();
}

/**
 * Unified review tile used on /reviews and every <RelatedReviews> strip.
 * Same height across a row (via h-full on parent + flex-col here). Long
 * bodies clamp at 6 lines with a gradient fade + Read more button that
 * opens the review in a modal.
 *
 * @param {object}  review          Review object from /data/reviews.json
 * @param {object}  [motionVariant] Framer Motion variants for staggered entrance.
 * @param {"default"|"compact"} [size]
 * @param {string}  [testId]        Override for data-testid root.
 */
export default function ReviewTile({
  review,
  motionVariant,
  size = "default",
  testId,
  onSourceClick,
}) {
  const [open, setOpen] = useState(false);

  const preview = useMemo(() => firstParagraph(review.text), [review.text]);
  const isTruncated =
    (review.text || "").trim().length > preview.length ||
    preview.length > 260; // clamp fallback for single long paragraphs

  const pad = size === "compact" ? "p-4" : "p-5 sm:p-6";
  const textSize = size === "compact" ? "text-sm" : "text-[15px]";
  const clamp = size === "compact" ? "line-clamp-5" : "line-clamp-6";

  return (
    <>
      <motion.article
        variants={motionVariant}
        className={`group relative flex flex-col ${pad} rounded-2xl border border-brand-light-grey bg-white h-full transition-all duration-200 hover:border-brand-aqua/50 hover:shadow-lg hover:-translate-y-0.5`}
        data-testid={testId || `review-tile-${review.source}`}
      >
        {/* Header — rating + source */}
        <div className="flex items-center justify-between mb-3">
          <Stars rating={review.rating} size={size === "compact" ? 13 : 14} />
          <SourceBadge source={review.source} size={size === "compact" ? 12 : 14} />
        </div>

        {/* Optional title */}
        {review.title && (
          <h3 className="font-heading text-sm font-bold text-brand-black mb-2 leading-snug line-clamp-2">
            {review.title}
          </h3>
        )}

        {/* Body — first paragraph, clamped. Fade mask prevents hard cut-off. */}
        <div className="relative flex-1 min-h-0">
          <p className={`${textSize} text-brand-grey leading-relaxed ${clamp}`}>
            {preview}
          </p>
          {isTruncated && (
            <div
              className="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-white to-transparent"
              aria-hidden="true"
            />
          )}
        </div>

        {/* Read more affordance — always same vertical spot so tiles align */}
        <div className="mt-3 min-h-[20px]">
          {isTruncated && (
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="inline-flex items-center gap-1 text-xs font-semibold text-brand-aqua-dark hover:text-brand-black transition-colors"
              data-testid={`review-tile-read-more-${review.source}`}
              aria-label={`Read full review by ${review.author}`}
            >
              Read more
              <ArrowUpRight size={12} />
            </button>
          )}
        </div>

        {/* Footer — author + date */}
        <div className="mt-4 pt-3 border-t border-brand-light-grey/60 flex items-center gap-3">
          <Avatar name={review.author} size={size === "compact" ? "sm" : "md"} />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-brand-black truncate">
              {review.author}
            </p>
            {review.dateLabel && (
              <p className="text-[11px] text-brand-grey">{review.dateLabel}</p>
            )}
          </div>
          <a
            href={review.profileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-shrink-0 p-1 -m-1 text-brand-grey hover:text-brand-aqua-dark transition-colors"
            aria-label={`View on ${review.source === "trustpilot" ? "Trustpilot" : "Google"}`}
            onClick={(e) => {
              e.stopPropagation();
              if (onSourceClick) onSourceClick(review);
            }}
          >
            <ArrowUpRight size={14} />
          </a>
        </div>

        {/* Tags row — industries + products, brand colours, always bottom */}
        {(review.industries?.length > 0 || review.products?.length > 0) && (
          <div className="mt-4 pt-3 border-t border-brand-light-grey/60">
            <TagRow
              industries={review.industries || []}
              products={review.products || []}
              compact={size === "compact"}
            />
          </div>
        )}
      </motion.article>

      {open && (
        <ReviewModal
          review={review}
          onClose={() => setOpen(false)}
          onSourceClick={onSourceClick}
        />
      )}
    </>
  );
}
